import { computed, shallowRef } from 'vue'

export const localSaveFailures = shallowRef<Record<string, string>>({})
const retries = new Map<string, () => Promise<unknown>>()
const writes = new Map<string, Promise<unknown>>()
export const hasLocalSaveFailure = computed(() => Object.keys(localSaveFailures.value).length > 0)

export function localSaveFailed(id: string, cause: unknown, retry: () => Promise<unknown>): void {
  localSaveFailures.value = { ...localSaveFailures.value, [id]: cause instanceof Error ? cause.message : String(cause) }
  retries.set(id, retry)
}
export function localSaveSucceeded(id: string): void {
  const next = { ...localSaveFailures.value }
  delete next[id]
  localSaveFailures.value = next
  retries.delete(id)
}
export async function retryLocalSaves(): Promise<void> {
  await Promise.allSettled([...retries.values()].map(retry => retry()))
}
/** All callers for the same page/file share a write order, even across editor instances. */
export function serializeLocalWrite<T>(id: string, operation: () => Promise<T>): Promise<T> {
  const task = (writes.get(id) ?? Promise.resolve()).catch(() => {}).then(() => {
    if (savingSuspended) throw new Error('Local data is being removed')
    return operation()
  })
  writes.set(id, task)
  void task.finally(() => { if (writes.get(id) === task) writes.delete(id) }).catch(() => {})
  return task
}
export function requireWriteSuccess(result: { success?: boolean; error?: string } | undefined): void {
  if (!result?.success) throw new Error(result?.error || 'Local storage did not confirm the write')
}

export function discardLocalSaveRetries(): void {
  retries.clear()
  localSaveFailures.value = {}
}

let savingSuspended = false
export function localSavingIsSuspended(): boolean { return savingSuspended }
export function suspendLocalSaving(): void { savingSuspended = true }
export function resumeLocalSaving(): void { savingSuspended = false }
export async function drainLocalWrites(): Promise<void> {
  while (writes.size) await Promise.allSettled([...writes.values()])
}
